// src/pages/PhoneVerificationContainer.jsx

import React, { useState } from "react";
import styled from "styled-components";
import { AXIOS_POST } from "../config/axios";
import Loader from "../helper/Loader";
import { useNavigate } from "react-router-dom";
import ErrorBox from "../helper/Messages/Error";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Translate from "../config/Translate";
import { useSelector } from "react-redux";
import { selectLanguage } from "../features/LanguageSlice";
import { companyInfo } from "../helper/Company";
import LanguageSwitch from "./languageSwitch";
import En from "../helper/Dictionary/En";
import Fr from "../helper/Dictionary/Fr";

// Country codes data
const COUNTRY_CODES = [
  // North America
  { code: "+1", country: "US", flag: "🇺🇸", name: "United States" },

  // Europe
  { code: "+44", country: "GB", flag: "🇬🇧", name: "United Kingdom" },
  { code: "+33", country: "FR", flag: "🇫🇷", name: "France" },
  { code: "+49", country: "DE", flag: "🇩🇪", name: "Germany" },
  { code: "+39", country: "IT", flag: "🇮🇹", name: "Italy" },
  { code: "+34", country: "ES", flag: "🇪🇸", name: "Spain" },
  { code: "+31", country: "NL", flag: "🇳🇱", name: "Netherlands" },
  { code: "+32", country: "BE", flag: "🇧🇪", name: "Belgium" },
  { code: "+41", country: "CH", flag: "🇨🇭", name: "Switzerland" },
  { code: "+43", country: "AT", flag: "🇦🇹", name: "Austria" },
  { code: "+45", country: "DK", flag: "🇩🇰", name: "Denmark" },
  { code: "+46", country: "SE", flag: "🇸🇪", name: "Sweden" },
  { code: "+47", country: "NO", flag: "🇳🇴", name: "Norway" },
  { code: "+358", country: "FI", flag: "🇫🇮", name: "Finland" },
  { code: "+351", country: "PT", flag: "🇵🇹", name: "Portugal" },
  { code: "+30", country: "GR", flag: "🇬🇷", name: "Greece" },
  { code: "+48", country: "PL", flag: "🇵🇱", name: "Poland" },
  { code: "+420", country: "CZ", flag: "🇨🇿", name: "Czech Republic" },
  { code: "+36", country: "HU", flag: "🇭🇺", name: "Hungary" },
  { code: "+40", country: "RO", flag: "🇷🇴", name: "Romania" },
  { code: "+353", country: "IE", flag: "🇮🇪", name: "Ireland" },
  { code: "+354", country: "IS", flag: "🇮🇸", name: "Iceland" },

  // Asia
  { code: "+91", country: "IN", flag: "🇮🇳", name: "India" },
  { code: "+92", country: "PK", flag: "🇵🇰", name: "Pakistan" },

  // Oceania
  { code: "+61", country: "AU", flag: "🇦🇺", name: "Australia" },
];

const PhoneVerificationContainer = () => {
  const [countryCode, setCountryCode] = useState("+41");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const [errors, setErrors] = useState(null);
  const navigate = useNavigate();
  const [displayCode, setDisplayCode] = useState("+41");
  const tokenString = localStorage.getItem("token");
  const token =
    tokenString && tokenString !== "undefined"
      ? JSON.parse(tokenString)?.access_token
      : null;

  const languageValue = useSelector(selectLanguage);
  const cinfo = companyInfo();

  // Get placeholders based on language
  const pPhoneNumber =
    languageValue === "En"
      ? En["page.phoneVerificationPlaceholder"]
      : Fr["page.phoneVerificationPlaceholder"];

  const validatePhoneNumber = (phone) => {
    const cleanPhone = phone.replace(/\D/g, "");
    return cleanPhone.length >= 7 && cleanPhone.length <= 15;
  };

  const handlePhoneSubmit = (e) => {
    e.preventDefault();
    setErrors(null);

    if (!phoneNumber.trim()) {
      languageValue === "En"
        ? setErrors("Please enter your phone number")
        : setErrors("Veuillez saisir votre numéro de téléphone");
      return;
    }

    if (!validatePhoneNumber(phoneNumber)) {
      languageValue === "En"
        ? setErrors("Please enter a valid phone number")
        : setErrors("Veuillez saisir un numéro de téléphone valide");
      return;
    }

    setShowLoader(true);

    const fullPhoneNumber = `${countryCode}${phoneNumber}`;
    localStorage.setItem("verificationPhone", fullPhoneNumber);

    const SMS_OTP_ENDPOINT = "/send-sms-otp";
    const userEmail = localStorage.getItem("userEmail");
    console.log("User Email:", userEmail);
    console.log("Full Phone Number:", fullPhoneNumber);

    AXIOS_POST(
      SMS_OTP_ENDPOINT,
      { phone: fullPhoneNumber, email: userEmail },
      token,
      languageValue === "En" ? "en" : "fr"
    )
      .then((result) => {
        setShowLoader(false);
        languageValue === "En"
          ? toast.success("OTP sent to your phone successfully")
          : toast.success("OTP envoyé à votre téléphone avec succès");

        console.log(result.data);

        setTimeout(() => {
          navigate("/otp", {
            state: {
              verificationType: "phone",
              phoneNumber: fullPhoneNumber,
              otpData: result.data,
            },
          });
        }, 2000);
      })
      .catch((e) => {
        setShowLoader(false);
        if (e.response?.data?.message) {
          setErrors(e.response.data.message);
        } else {
          languageValue === "En"
            ? setErrors("Failed to send OTP. Please try again!")
            : setErrors("Échec de l'envoi de l'OTP. Veuillez réessayer!");
        }
      });
  };

  const handleBackToEmail = () => {
    navigate("/confirm_email");
  };

  return (
    <Container>
      <ToastContainer />
      <PageIntro>
        <ImgContainer src={cinfo.CimagePath} alt="logo" />
        <Title>
          <Translate Word="page.phoneVerificationTitle" />
        </Title>
        <SubTitle>
          <Translate Word="page.phoneVerificationSubtitle" />
        </SubTitle>
      </PageIntro>

      <Form onSubmit={handlePhoneSubmit}>
        <InputGroup>
          <Label>
            <Translate Word="page.phoneNumberLabel" />
          </Label>
     <PhoneInputWrapper>
  <CountryCodeSelect
    value={countryCode}
    onChange={(e) => {
      setCountryCode(e.target.value);
    }}
  >
    {COUNTRY_CODES.map((country) => (
      <option key={country.code} value={country.code}>
        {country.country} {country.name} ({country.code})
      </option>
    ))}
  </CountryCodeSelect>

  <PhoneNumberContainer>
    <CodeDisplay>{countryCode}</CodeDisplay>

    <PhoneInput
      type="tel"
      placeholder={pPhoneNumber}
      value={phoneNumber}
      onChange={(e) => {
        const value = e.target.value.replace(/[^0-9\s\-]/g, "");
        setPhoneNumber(value);
      }}
      maxLength="15"
    />
  </PhoneNumberContainer>
</PhoneInputWrapper>
          <HelpText>
            <Translate Word="page.phoneExampleText" />: {countryCode} 3001234567
          </HelpText>
        </InputGroup>

        {errors && <ErrorBox Message={errors} />}

        <ButtonGroup>
          <SubmitButton type="submit" disabled={showLoader}>
            {showLoader ? <Loader /> : <Translate Word="page.sendOTPButton" />}
          </SubmitButton>
        </ButtonGroup>
      </Form>

      <AlternativeOptions>
        <BackLink onClick={handleBackToEmail}>
          ← <Translate Word="page.backToEmailVerification" />
        </BackLink>
      </AlternativeOptions>

      <LanguageSwitch />
      <Copyright>© {new Date().getFullYear()} Digimidi</Copyright>
    </Container>
  );
};

export default PhoneVerificationContainer;

// Styled Components
const PhoneNumberContainer = styled.div`
  border: 2px solid #3b82f6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;
const CodeDisplayInput = styled.input`
  padding: 12px 15px;
  font-size: 16px;
  border: 1px solid #4ecdc4;
  border-radius: 6px;
  height: 48px;
  color: rgb(89, 89, 89);
  background-color: rgb(249, 249, 249);
  width: 80px;
  text-align: center;
  font-weight: 500;
  cursor: default;

  &:focus {
    outline: none;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;
const Container = styled.div`
  position: relative;
  color: rgba(0, 0, 0, 0.85);
  max-width: 450px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ImgContainer = styled.img`
  width: 150px;
  object-fit: cover;
`;

const PageIntro = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
  text-align: center;
`;

const Title = styled.h3`
  color: rgba(0, 0, 0, 0.85);
  font-weight: 700;
  margin-bottom: 10px;
  margin-top: 20px;
`;

const SubTitle = styled.p`
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
  margin-bottom: 10px;
`;

const Form = styled.form`
  width: 100%;
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
`;

const PhoneInputWrapper = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  align-items: stretch;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
  }
`;

const CodeDisplay = styled.div`
  padding: 12px 15px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 8px 0 0 8px;
  height: 52px;
  color: rgb(89, 89, 89);
  background-color: rgb(255, 255, 255);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 70px;
  flex-shrink: 0;
  border-right: 1px solid #e8e8e8;

  @media (max-width: 768px) {
    min-width: 70px;
    width: auto;
  }
`;

const CountryCodeSelect = styled.select`
  padding: 12px 15px;
  font-size: 14px;
  border: 2px solid #3b82f6;
  border-radius: 8px;
  height: 52px;
  color: rgb(89, 89, 89);
  background-color: rgb(255, 255, 255);
  transition: all 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);
  outline: none;
  cursor: pointer;
  width: 120px;
  flex-shrink: 0;
  box-sizing: border-box;

  &:focus {
    border-color: #40a9ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }

  option {
    padding: 8px;
    font-size: 14px;
  }

  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
    flex-shrink: 1;
  }
`;

const PhoneInput = styled.input`
  flex: 1;
  padding: 12px 15px;
  font-size: 16px;
  border: none;
  border-radius: 0 8px 8px 0;
  height: 52px;
  line-height: 1.5;
  color: rgb(89, 89, 89);
  background-color: rgb(255, 255, 255);
  transition: all 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);
  outline: none;
  box-sizing: border-box;
  min-width: 0;
  width: 100%;

  &:focus {
    outline: none;
    border: none;
  }
  
  &::placeholder {
    color: rgba(0, 0, 0, 0.45);
  }

  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
  }
`;

const HelpText = styled.p`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-top: 6px;
  margin-bottom: 0;
`;

const ButtonGroup = styled.div`
  margin-bottom: 20px;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  height: 48px;
  cursor: pointer;
  color: #fff;
  background: #1890ff;
  border-color: #1890ff;
  font-weight: 500;
  font-size: 16px;
  border-radius: 6px;
  outline: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: #40a9ff;
    border-color: #40a9ff;
  }

  &:disabled {
    background: #f5f5f5;
    border-color: #d9d9d9;
    color: rgba(0, 0, 0, 0.25);
    cursor: not-allowed;
  }
`;

const AlternativeOptions = styled.div`
  text-align: center;
  margin: 20px 0;
`;

const BackLink = styled.p`
  color: #1890ff;
  cursor: pointer;
  font-size: 14px;
  margin: 0;

  &:hover {
    color: #40a9ff;
    text-decoration: underline;
  }
`;

const Copyright = styled.div`
  color: rgba(0, 0, 0, 0.45);
  margin-top: auto;
  text-align: center;
  font-size: 12px;
`;
